import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import STRING_KEYS from '../../../../language/keys';
import { COMPONENT_NAME, EVENT_GROUP } from '../../../../consts/analytics';
import { PAGE_NAMES } from '../../../../consts/pages';
import { CREATE_POSITION_ANALYTICS_EVENTS } from '../../consts/position-analytics.consts';
import { useAnalyticsContext } from '../../../../contexts/analytics/useAnalyticsContext';
import {
  PageTitleBold,
} from '../create-position-container/create-position-container.styled';
import { CreatePositionContainer } from '../create-position-container/create-position-container.component';
import ExperienceItem
  from '../experience-item/experience-item.component';
import { LEVEL_OF_EXPERIENCE_ICONS, LEVEL_OF_EXPERIENCE_YEARS } from '../../consts/create-position.consts';
import { LevelOfExperience } from '../../enums/create-position-experience-level.enums';
import {
  addExperienceLevel,
  removeExperienceLevel,
} from '../../../../store/slices/create-position-state/create-position-state.toolkit-slice';
import { useCreatePositionLevelsOfExperienceSelector } from '../../../../store/slices/create-position-state/selectors/create-position-level-experience.selector';
import { useGetStepInfoComponent } from '../../hooks/create-position-actions.hooks';
import { ExperienceItemWrapper } from './create-position-experience-step.styled';

type CreatePositionExperienceProps = {
  stepNumber: number,
  setContinueButtonEnable: () => unknown,
  setContinueButtonDisable: () => unknown,
  setContinueButtonShow: () => unknown,
}

export const CreatePositionExperienceStep = ({
  stepNumber,
  setContinueButtonEnable,
  setContinueButtonDisable,
  setContinueButtonShow,
}: CreatePositionExperienceProps): JSX.Element => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const InfoComponent = useGetStepInfoComponent();
  const { analyticsTrackEvent } = useAnalyticsContext();

  const { selectedLevelsOfExperience } = useCreatePositionLevelsOfExperienceSelector();

  const selectedLevelsOfExperienceSet = useMemo(() => new Set(selectedLevelsOfExperience), [selectedLevelsOfExperience]);

  useEffect(() => {
    if (selectedLevelsOfExperience?.length) {
      dispatch(setContinueButtonEnable());
      dispatch(setContinueButtonShow());
    } else {
      dispatch(setContinueButtonDisable());
    }
  }, [dispatch, selectedLevelsOfExperience, setContinueButtonDisable, setContinueButtonEnable, setContinueButtonShow]);

  const experienceItems = [
    {
      id: LevelOfExperience.StartingOut,
      experienceTitle: translate(STRING_KEYS.LEVEL_OF_EXPERIENCE[LevelOfExperience.StartingOut]),
      experienceDescription: translate(
        STRING_KEYS.ONBOARDING.ONBOARDING_EXPERIENCE_TOTAL_YEARS,
        LEVEL_OF_EXPERIENCE_YEARS.STARTING_IN_OUT,
      ),
      experienceIcon: LEVEL_OF_EXPERIENCE_ICONS.STARTING_IN_OUT,
    },
    {
      id: LevelOfExperience.SomewhatExperienced,
      experienceTitle: translate(STRING_KEYS.LEVEL_OF_EXPERIENCE[LevelOfExperience.SomewhatExperienced]),
      experienceDescription: translate(
        STRING_KEYS.ONBOARDING.ONBOARDING_EXPERIENCE_TOTAL_YEARS,
        LEVEL_OF_EXPERIENCE_YEARS.SOMEWHAT_EXPERIENCED,
      ),
      experienceIcon: LEVEL_OF_EXPERIENCE_ICONS.SOMEWHAT_EXPERIENCED,
    },
    {
      id: LevelOfExperience.HighlyExperienced,
      experienceTitle: translate(STRING_KEYS.LEVEL_OF_EXPERIENCE[LevelOfExperience.HighlyExperienced]),
      experienceDescription: translate(
        STRING_KEYS.ONBOARDING.ONBOARDING_EXPERIENCE_TOTAL_YEARS,
        LEVEL_OF_EXPERIENCE_YEARS.HIGHLY_EXPERIENCED,
      ),
      experienceIcon: LEVEL_OF_EXPERIENCE_ICONS.HIGHLY_EXPERIENCED,
    },
    {
      id: LevelOfExperience.IncrediblyExperienced,
      experienceTitle: translate(STRING_KEYS.LEVEL_OF_EXPERIENCE[LevelOfExperience.IncrediblyExperienced]),
      experienceDescription: `${LEVEL_OF_EXPERIENCE_YEARS.INCREDIBLY_EXPERIENCED}
      ${translate(STRING_KEYS.YEARS)}`,
      experienceIcon: LEVEL_OF_EXPERIENCE_ICONS.INCREDIBLY_EXPERIENCED,
    },
  ];

  const handleClickExperienceItem = (itemId: LevelOfExperience) => {
    const trackEventPayload = {
      eventName: CREATE_POSITION_ANALYTICS_EVENTS.NEW_POSITION_INSERT_PARAMETER_CLICKED,
      eventGroup: EVENT_GROUP.CLICK,
      stepNumber,
      pageName: PAGE_NAMES.EXPERIENCE_PAGE_VIEWED,
      componentName: COMPONENT_NAME.EXPERIENCE_LEVEL,
      componentMetadata: itemId,
    };

    if (selectedLevelsOfExperienceSet.has(itemId)) {
      dispatch(removeExperienceLevel(itemId));
    } else {
      dispatch(addExperienceLevel(itemId));
    }
    analyticsTrackEvent(trackEventPayload);
  };

  const renderExperienceItem = () => (experienceItems.map((experienceItem) => (
    <ExperienceItemWrapper key={ experienceItem.id } >
      <ExperienceItem
        itemId={ experienceItem.id }
        onClick={ handleClickExperienceItem }
        title={ experienceItem.experienceTitle }
        description={ experienceItem.experienceDescription }
        emoji={ experienceItem.experienceIcon }
        selected={ selectedLevelsOfExperienceSet.has(experienceItem.id) }
      />
    </ExperienceItemWrapper>
  )));

  const getPageTitle = () => (
    <Trans
      i18nKey={ STRING_KEYS.ONBOARDING.ONBOARDING_EXPERIENCE_PAGE_TITLE }
    >
      <PageTitleBold />
    </Trans>
  );

  return (
    <>
      <CreatePositionContainer
        title={ getPageTitle() }
        info={ InfoComponent }
      >
        { renderExperienceItem() }
      </CreatePositionContainer>
    </>
  );
};
